import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowLeft, Copy, Check, Trash2, Loader2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { apiKeysApi } from "@/lib/account";

function CodeBlock({ children, copyValue, testid }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(copyValue ?? children);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Copy failed");
    }
  };
  return (
    <div className="flex items-center gap-3 bg-[var(--bg-elevated)] border border-[var(--border)] px-4 py-3">
      <code
        data-testid={testid}
        className="font-mono-ui text-[12px] text-[var(--text)] flex-1 break-all"
      >
        {children}
      </code>
      <button
        onClick={onCopy}
        data-testid={`${testid}-copy`}
        className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text-muted)] hover:text-[var(--accent)] inline-flex items-center gap-1.5 transition-colors"
      >
        {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

export default function AccountApiKeys() {
  const { user } = useAuth();
  const [keys, setKeys] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [revealed, setRevealed] = useState(null); // {key, name, key_prefix, id}
  const [busyId, setBusyId] = useState(null);

  const refresh = () =>
    apiKeysApi
      .list()
      .then(setKeys)
      .catch((e) => toast.error(e.message || "Failed to load keys"));

  useEffect(() => {
    if (user && typeof user === "object") refresh();
  }, [user]);

  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg)] text-[var(--text-muted)] font-mono-ui text-[11px] tracking-[0.3em] uppercase">
        Loading…
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" state={{ from: "/account/api-keys" }} replace />;
  if (user && user.api_keys_enabled === false) return <Navigate to="/" replace />;

  const onCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim() || creating) return;
    setCreating(true);
    try {
      const created = await apiKeysApi.create({ name: newName.trim() });
      setRevealed(created);
      setNewName("");
      await refresh();
    } catch (err) {
      toast.error(err.message || "Failed to create key");
    } finally {
      setCreating(false);
    }
  };

  const onRevoke = async (id) => {
    if (!confirm("Revoke this key? Any service using it will break immediately.")) return;
    setBusyId(id);
    try {
      await apiKeysApi.revoke(id);
      await refresh();
    } catch (err) {
      toast.error(err.message || "Failed to revoke");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)]">
      <header className="border-b border-[var(--border)] px-6 md:px-10 py-5 flex items-center justify-between">
        <Link
          to="/"
          data-testid="account-back"
          className="font-mono-ui text-[11px] tracking-[0.28em] uppercase text-[var(--text-muted)] hover:text-[var(--text)] inline-flex items-center gap-2 transition-colors"
        >
          <ArrowLeft size={13} /> Back to chat
        </Link>
        <h1 className="font-mono-ui text-[11px] tracking-[0.4em] uppercase">
          API Keys
        </h1>
        <Link
          to="/account/usage"
          data-testid="keys-to-usage"
          className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--accent)] hover:underline"
        >
          Usage →
        </Link>
      </header>

      <main className="max-w-3xl mx-auto px-6 md:px-10 pb-20">
        <section className="py-12 border-b border-[var(--border)]">
          <KeyRound size={28} className="text-[var(--accent)] mb-5" />
          <h2 className="font-serif text-3xl text-[var(--text)] mb-3">
            Programmatic access to the Humane Behavior API.
          </h2>
          <p className="font-sans text-sm text-[var(--text-muted)] max-w-xl leading-relaxed">
            Each key has its own request-per-minute and monthly request budget.
            Use them to call <code className="font-mono-ui text-[12px] text-[var(--text)]">POST /api/v1/chat</code> from
            your own services. The full key is shown <em>once</em> at creation —
            store it in your secrets manager immediately.
          </p>
        </section>

        {/* Reveal modal */}
        {revealed && (
          <section
            data-testid="reveal-banner"
            className="border border-[var(--accent)] mt-10 p-6 bg-[var(--bg-elevated)]"
          >
            <div className="font-mono-ui text-[10px] tracking-[0.32em] uppercase text-[var(--accent)] mb-3">
              Your new key — copy now, this is the only time you'll see it
            </div>
            <div className="font-serif text-xl text-[var(--text)] mb-4">{revealed.name}</div>
            <CodeBlock copyValue={revealed.key} testid="revealed-key">{revealed.key}</CodeBlock>
            <button
              onClick={() => setRevealed(null)}
              data-testid="reveal-dismiss"
              className="mt-5 font-mono-ui text-[10px] tracking-[0.28em] uppercase px-4 py-2 border border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors"
            >
              I've copied it
            </button>
          </section>
        )}

        {/* Create */}
        <section className="py-10 border-b border-[var(--border)]">
          <div className="font-mono-ui text-[11px] tracking-[0.34em] uppercase text-[var(--text-muted)] mb-5">
            Create a new key
          </div>
          <form onSubmit={onCreate} className="flex flex-wrap gap-3">
            <input
              type="text"
              placeholder="e.g. production server"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              data-testid="new-key-name"
              maxLength={80}
              className="flex-1 min-w-[240px] bg-[var(--bg-elevated)] border border-[var(--border)] px-4 py-2.5 font-ui text-sm text-[var(--text)] placeholder:text-[var(--text-dim)] focus:outline-none focus:border-[var(--accent)] transition-colors"
            />
            <button
              type="submit"
              disabled={creating || !newName.trim()}
              data-testid="new-key-submit"
              className="font-mono-ui text-[10px] tracking-[0.28em] uppercase px-5 py-2.5 border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-[var(--bg)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-2"
            >
              {creating && <Loader2 className="animate-spin" size={12} />}
              {creating ? "Creating…" : "Generate key"}
            </button>
          </form>
        </section>

        {/* List */}
        <section className="py-10">
          <div className="font-mono-ui text-[11px] tracking-[0.34em] uppercase text-[var(--text-muted)] mb-5">
            Your keys ({keys?.length ?? 0})
          </div>
          {keys === null && (
            <p className="font-sans text-sm text-[var(--text-dim)]">Loading…</p>
          )}
          {keys && keys.length === 0 && (
            <p data-testid="no-keys" className="font-sans text-sm text-[var(--text-dim)]">
              No keys yet. Create one above.
            </p>
          )}
          <div className="space-y-3">
            {keys?.map((k) => (
              <div
                key={k.id}
                data-testid={`key-row-${k.id}`}
                className="border border-[var(--border)] px-5 py-4 flex flex-wrap items-center gap-4"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-ui text-[var(--text)] truncate">
                    {k.name}{" "}
                    {!k.active && (
                      <span className="font-mono-ui text-[9px] tracking-[0.2em] uppercase text-red-400 ml-2">
                        revoked
                      </span>
                    )}
                  </div>
                  <div className="font-mono-ui text-[10px] tracking-[0.18em] text-[var(--text-dim)] mt-1.5 break-all">
                    {k.key_prefix}…
                  </div>
                  <div className="font-mono-ui text-[10px] tracking-[0.18em] text-[var(--text-dim)] mt-1">
                    {k.requests_per_minute} req/min
                    {" · "}
                    {k.monthly_token_cap === null
                      ? "unlimited"
                      : `${k.tokens_used_this_month} / ${k.monthly_token_cap} this month`}
                    {k.last_used_at && ` · last used ${k.last_used_at.slice(0, 10)}`}
                  </div>
                </div>
                <button
                  onClick={() => onRevoke(k.id)}
                  disabled={busyId === k.id}
                  data-testid={`revoke-${k.id}`}
                  className="font-mono-ui text-[10px] tracking-[0.24em] uppercase px-3 py-1.5 border border-[var(--border)] hover:border-red-500 hover:text-red-400 disabled:opacity-40 inline-flex items-center gap-1.5 transition-colors"
                >
                  <Trash2 size={11} /> Revoke
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* Usage hint */}
        <section className="py-8 border-t border-[var(--border)]">
          <div className="font-mono-ui text-[11px] tracking-[0.34em] uppercase text-[var(--text-muted)] mb-4">
            Quick test
          </div>
          <CodeBlock testid="curl-example">{`curl -X POST ${window.location.origin}/api/v1/chat \\
  -H "Authorization: Bearer hbai_sk_..." \\
  -H "Content-Type: application/json" \\
  -d '{"messages":[{"role":"user","content":"Hello"}]}'`}</CodeBlock>
        </section>

        {/* SLO health endpoint reference */}
        <section className="py-8 border-t border-[var(--border)]">
          <div className="font-mono-ui text-[11px] tracking-[0.34em] uppercase text-[var(--text-muted)] mb-4">
            Service status (SLO)
          </div>
          <p className="font-sans text-sm text-[var(--text-muted)] leading-relaxed mb-4 max-w-2xl">
            Programmatic health snapshot — same telemetry the operator sees,
            scrubbed of internals. Poll this on a 30-second interval to build
            your own SLO dashboards and alerting.
            Returns <code className="font-mono-ui text-[12px] text-[var(--text)]">status</code>
            {" "}(<code className="text-emerald-400">operational</code> /
            {" "}<code className="text-yellow-400">degraded</code> /
            {" "}<code className="text-red-400">outage</code>),
            success-path <code className="font-mono-ui text-[12px] text-[var(--text)]">p50</code>/
            <code className="font-mono-ui text-[12px] text-[var(--text)]">p95</code> latency,
            success rate, and your key&apos;s monthly-cap headroom.
          </p>
          <CodeBlock testid="curl-health-example">{`curl ${window.location.origin}/api/v1/health?window_minutes=60 \\
  -H "Authorization: Bearer hbai_sk_..."`}</CodeBlock>
          <p className="font-mono-ui text-[10px] tracking-[0.2em] uppercase text-[var(--text-dim)] mt-3">
            Query params: window_minutes (1–1440, default 60)
          </p>
        </section>
      </main>
    </div>
  );
}
